import React from 'react';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';

const { FiMapPin, FiNavigation, FiClock, FiTruck, FiCamera } = FiIcons;

const Location = () => {
  const [ref, inView] = useInView({
    threshold: 0.2,
    triggerOnce: true
  });

  const transportOptions = [
    {
      icon: FiTruck,
      title: 'By Bus (Budget Option)',
      description: 'Take Route 358 (Dellawa - Galle). We are right on the route.',
      time: '3-4 hours from Colombo',
      cost: 'LKR 200-300'
    },
    {
      icon: FiNavigation,
      title: 'By Car (Scenic Route)',
      description: 'Accessible via Warukandeniya or the scenic Kolonna route.',
      time: '2.5 hours from Colombo',
      cost: 'Fuel + Tolls'
    }
  ];

  const attractions = [
    {
      name: 'Dellawa Bathing Place',
      distance: '3 minutes walk',
      description: 'Crystal clear natural pools in the Gin Ganga river',
      image: 'https://images.unsplash.com/photo-1506905925346-21bda4d32df4?ixlib=rb-4.0.3&auto=format&fit=crop&w=400&q=80'
    },
    {
      name: 'Sinharaja Morningside Entrance',
      distance: '40 km drive',
      description: 'Less crowded entrance to the UNESCO World Heritage site',
      image: 'https://images.unsplash.com/photo-1441974231531-c6227db76b6e?ixlib=rb-4.0.3&auto=format&fit=crop&w=400&q=80'
    },
    {
      name: 'Neluwa Doovili Ella',
      distance: '1 hour drive',
      description: 'Spectacular waterfall perfect for day trips',
      image: 'https://images.unsplash.com/photo-1506905925346-21bda4d32df4?ixlib=rb-4.0.3&auto=format&fit=crop&w=400&q=80'
    }
  ];

  const handleGetDirections = () => {
    const address = encodeURIComponent('Villa Springwater, No. 2/1 Andadola, Dellawa, Warukandeniya, Sri Lanka');
    window.open(`https://maps.google.com/maps?q=${address}`, '_blank');
  };

  const handleTransportHelp = () => {
    const message = encodeURIComponent("Hi! I need help with transportation to Villa Springwater. Can you provide detailed directions?");
    window.open(`https://wa.me/94704003478?text=${message}`, '_blank');
  };

  return (
    <section id="location" className="relative py-20 overflow-hidden">
      {/* Background Pattern */}
      <div className="absolute inset-0 opacity-5">
        <div className="absolute inset-0" style={{
          backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23059669' fill-opacity='0.4'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`
        }} />
      </div>

      <div ref={ref} className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <motion.div
          className="text-center mb-16"
          initial={{ opacity: 0, y: 50 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8 }}
        >
          <motion.h2
            className="text-4xl md:text-5xl font-bold text-white mb-6"
            initial={{ opacity: 0, y: 30 }}
            animate={inView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.8, delay: 0.2 }}
          >
            Location &
            <span className="gradient-primary bg-clip-text text-transparent ml-3">
              Adventure
            </span>
          </motion.h2>
          <motion.p
            className="text-xl text-gray-300 max-w-3xl mx-auto"
            initial={{ opacity: 0, y: 20 }}
            animate={inView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.8, delay: 0.4 }}
          >
            Perfectly positioned between Udawalawe Safari and Galle Fort. Take the scenic route through the heart of Sri Lanka's rainforest corridor.
          </motion.p>
        </motion.div>

        {/* Strategic Location Highlight */}
        <motion.div
          className="glass-morphism rounded-3xl p-8 mb-16 text-center"
          initial={{ opacity: 0, scale: 0.9 }}
          animate={inView ? { opacity: 1, scale: 1 } : {}}
          transition={{ duration: 0.8, delay: 0.6 }}
        >
          <div className="flex items-center justify-center mb-4">
            <SafeIcon icon={FiMapPin} className="text-emerald-400 text-3xl" />
          </div>
          <h3 className="text-2xl font-bold text-white mb-4">The Perfect Stopover</h3>
          <p className="text-gray-300 text-lg max-w-2xl mx-auto">
            <span className="text-emerald-400 font-semibold">No. 2/1 Andadola, Dellawa, Warukandeniya</span>
            <br />
            The scenic route between Udawalawe Safari and Galle Fort. Save time and discover hidden beauty.
          </p>
        </motion.div>

        {/* Transport Options */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-16">
          {transportOptions.map((option, index) => (
            <motion.div
              key={index}
              className="glass-dark rounded-2xl p-8"
              initial={{ opacity: 0, x: index === 0 ? -50 : 50 }}
              animate={inView ? { opacity: 1, x: 0 } : {}}
              transition={{ duration: 0.8, delay: 0.8 + index * 0.2 }}
              whileHover={{ scale: 1.02 }}
            >
              <div className="flex items-center gap-4 mb-4">
                <div className="w-12 h-12 bg-gradient-to-r from-emerald-500 to-teal-400 rounded-xl flex items-center justify-center">
                  <SafeIcon icon={option.icon} className="text-white text-xl" />
                </div>
                <h3 className="text-xl font-bold text-white">{option.title}</h3>
              </div>
              <p className="text-gray-300 mb-4">{option.description}</p>
              <div className="flex items-center gap-4 text-sm">
                <div className="flex items-center gap-2">
                  <SafeIcon icon={FiClock} className="text-emerald-400" />
                  <span className="text-gray-300">{option.time}</span>
                </div>
                <div className="text-emerald-400 font-semibold">{option.cost}</div>
              </div>
            </motion.div>
          ))}
        </div>

        {/* Map Section */}
        <motion.div
          className="glass-morphism rounded-3xl overflow-hidden mb-16"
          initial={{ opacity: 0, y: 50 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8, delay: 1.2 }}
        >
          <div className="p-8">
            <h3 className="text-2xl font-bold text-white mb-6 text-center">Find Your Way to Paradise</h3>
            
            {/* Embedded Map Placeholder */}
            <div className="relative h-64 md:h-80 bg-gray-800 rounded-2xl overflow-hidden">
              <div className="absolute inset-0 flex items-center justify-center">
                <div className="text-center">
                  <SafeIcon icon={FiMapPin} className="text-emerald-400 text-4xl mb-4 mx-auto" />
                  <p className="text-gray-300 mb-4">Interactive map will load here</p>
                  <motion.button
                    onClick={handleGetDirections}
                    className="gradient-primary text-white px-6 py-3 rounded-xl font-semibold hover:shadow-lg transition-all duration-300"
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                  >
                    Open in Google Maps
                  </motion.button>
                </div>
              </div>
            </div>

            {/* Transport Help */}
            <div className="text-center mt-6">
              <motion.button
                onClick={handleTransportHelp}
                className="glass-dark text-white px-6 py-3 rounded-xl font-medium hover:bg-white/20 transition-all duration-300"
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                Need Transport Help? WhatsApp Us
              </motion.button>
            </div>
          </div>
        </motion.div>

        {/* Nearby Attractions */}
        <motion.div
          initial={{ opacity: 0, y: 50 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8, delay: 1.4 }}
        >
          <h3 className="text-3xl font-bold text-white mb-8 text-center">
            Explore
            <span className="gradient-primary bg-clip-text text-transparent ml-2">
              Around Dellawa
            </span>
          </h3>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {attractions.map((attraction, index) => (
              <motion.div
                key={index}
                className="group relative overflow-hidden rounded-2xl"
                initial={{ opacity: 0, y: 30 }}
                animate={inView ? { opacity: 1, y: 0 } : {}}
                transition={{ duration: 0.6, delay: 1.6 + index * 0.1 }}
                whileHover={{ scale: 1.05 }}
              >
                {/* Image */}
                <div className="relative h-48 overflow-hidden">
                  <img
                    src={attraction.image}
                    alt={attraction.name}
                    className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent" />
                </div>

                {/* Content */}
                <div className="absolute bottom-0 left-0 right-0 p-6">
                  <div className="flex items-center gap-2 mb-2">
                    <SafeIcon icon={FiCamera} className="text-emerald-400" />
                    <span className="text-emerald-400 text-sm font-medium">{attraction.distance}</span>
                  </div>
                  <h4 className="text-lg font-bold text-white mb-2">{attraction.name}</h4>
                  <p className="text-gray-300 text-sm">{attraction.description}</p>
                </div>

                {/* Glass overlay on hover */}
                <div className="absolute inset-0 glass-morphism opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
              </motion.div>
            ))}
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default Location;